import { ActionTable } from '@/components';
import { Card } from '@/components/ui';
import { ProductionApi } from '@/state/api';
import { ProductionType } from '@/types';
import { Box, Center, Flex, Text } from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

type PartsForFrameProps = {
  frameId: number | undefined;
  showPartsForFrameFilterWithOnlyPrefixes?: string[];
  tableHeight?: string;
  rowHeight?: number;
};

type FrameDetailWrappedResponse =
  ProductionType.WorkOrderFrames.GetFrame['response'] & {
    data?: {
      id: number;
      name: string;
      quantity: number;
      project_id: number;
      release_id: number;
      frame_masterfile_id: number | null;
      frame_masterfile_revision_id: number | null;
      frame_drawing_url: string | null;
      parts: PartType[];
      work_orders: unknown[];
      history: unknown[];
      created_at: string;
      updated_at: string;
    };
  };

const PartsForFrame_Inner = ({
  frameId,
  showPartsForFrameFilterWithOnlyPrefixes,
  tableHeight = '55vh',
  rowHeight = 50,
}: PartsForFrameProps) => {
  const { data: frameResponse, isLoading: isLoadingFrameData } =
    ProductionApi.Api.useGetFrameQuery(
      frameId
        ? {
            params: {
              id: frameId,
            },
          }
        : skipToken,
    );

  const frameData = frameResponse as FrameDetailWrappedResponse | undefined;
  const frame = frameData?.data;

  const filteredParts = useMemo(() => {
    const parts = frame?.parts ?? [];

    if (
      !showPartsForFrameFilterWithOnlyPrefixes ||
      showPartsForFrameFilterWithOnlyPrefixes.length === 0
    ) {
      return parts;
    }

    return parts.filter((part) =>
      showPartsForFrameFilterWithOnlyPrefixes.some((prefix) =>
        part?.part_number?.startsWith(prefix),
      ),
    );
  }, [frame?.parts, showPartsForFrameFilterWithOnlyPrefixes]);

  return (
    <Flex flexDirection="column" rounded="sm" overflow="hidden">
      <Flex
        flexDirection="column"
        bg="gray.700"
        color="white"
        paddingX={4}
        paddingY={2}
      >
        <Text fontWeight="bold" color="white">
          Parts for Frame
        </Text>
      </Flex>

      <Card borderRadius={0}>
        <Center>
          <Flex flexDirection="column" gapY={2} minW={0} width="full">
            <ActionTable.FilterBar
              disabled={isLoadingFrameData || !frameId}
              filterConfigComponents={filterConfigComponents}
            />

            <Box
              key={`frame-parts-table-${frameId}`}
              display="flex"
              flexDirection="row"
              gap={5}
              width="full"
            >
              <Box h="100%" my={5} width="full">
                <ActionTable<PartType>
                  width="100%"
                  columns={columns}
                  columnToUseAsRowKey="part_id"
                  items={filteredParts}
                  isPaginated={false}
                  isPaginatedAsync={false}
                  totalItemsCount={filteredParts.length}
                  isSortable={true}
                  isFilterable={true}
                  isShowActionBar={false}
                  height={tableHeight}
                  rowHeight={rowHeight}
                  isLoading={isLoadingFrameData}
                  isMultipleSelection={false}
                  isSelectionDisabled
                  renderCell={renderCell}
                />
              </Box>
            </Box>
          </Flex>
        </Center>
      </Card>
    </Flex>
  );
};

const PartsForFrame = ({
  frameId,
  showPartsForFrameFilterWithOnlyPrefixes,
  tableHeight,
  rowHeight,
}: PartsForFrameProps) => {
  return (
    <ActionTable.Provider>
      <PartsForFrame_Inner
        frameId={frameId}
        showPartsForFrameFilterWithOnlyPrefixes={
          showPartsForFrameFilterWithOnlyPrefixes
        }
        tableHeight={tableHeight}
        rowHeight={rowHeight}
      />
    </ActionTable.Provider>
  );
};

export default PartsForFrame;

type PartType = {
  part_id: number;
  part_number: string;
  attributes?: Record<string, unknown>;
  required_per_frame: number;
  required_total: number;
  available_qty: number;
  short_qty: number;
};

const renderCell = (props: ActionTable.RenderCellType<PartType>) => {
  const { colKey, cellValue, rowValue } = props;

  switch (colKey) {
    case 'part_number':
      return (
        <Flex flexDirection="column">
          <Text fontWeight="bold">{rowValue.part_number}</Text>
        </Flex>
      );
    case 'required_total':
      return rowValue.required_total;
    case 'available_qty':
      return rowValue.available_qty;
    case 'short_qty':
      return (
        <Text color={rowValue.short_qty > 0 ? 'orange.400' : undefined}>
          {rowValue.short_qty}
        </Text>
      );
    default:
      return cellValue ?? '';
  }
};

const columns: ActionTable.ColumnsConfigType<PartType> = [
  {
    part_number: { label: 'Part Number', width: '220px' },
  },
  {
    required_per_frame: { label: 'Per Frame', width: '120px' },
  },
  {
    required_total: { label: 'Total Qty', width: '120px' },
  },
  {
    available_qty: { label: 'Available', width: '120px' },
  },
  {
    short_qty: { label: 'Short', width: '120px' },
  },
];

const filterConfigComponents: ActionTable.FilterConfigComponentType<PartType>[] =
  [
    {
      filterComponent: 'input',
      filterComponentLabel: 'Part Number',
      filterConfigItems: [
        {
          colKeys: ['part_number'],
          value: '',
          operator: 'contains',
        },
      ],
    },
  ];
